/**
 * Product page conversion helpers (loaded by sections/main-product.liquid).
 *
 * - "Save £X (Y%)" label next to the price, kept in sync as options change the price
 * - Sticky Add to cart bar mirrors the live price
 * - DPO (Dynamic Product Options) panel: live price in each step's button bar, step counter,
 *   a "More options" scroll cue when a step has more choices below, a "Back" button on every step after the first,
 *   a fabric samples link in the colour tab, and an Add to cart button on the last step
 *   (DPO shows no "Next Option" button there)
 *
 * Settings come from Theme settings > Product page via the #product-conversion-config JSON.
 */
(() => {
  const configEl = document.getElementById('product-conversion-config');
  if (!configEl) return;

  let config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const raf = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn();
      });
    };
  };

  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };

  /* ---------- Price saving + sticky bar price ---------- */

  const priceWrap = document.querySelector('.product-info__price');
  const savingEl = document.querySelector('.js-price-saving');
  const stickyPanel = document.querySelector('sticky-atc-panel');

  const parseMoney = (text) => {
    const cleaned = (text || '').replace(/[^\d.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
    const value = parseFloat(cleaned);
    return Number.isNaN(value) ? null : value;
  };

  const formatSaving = (amount, sample) => {
    const symbol = ((sample || '').match(/^[^\d]*/) || [''])[0].trim();
    const rounded = Math.round(amount * 100) / 100;
    const value = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    return `${symbol}${value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const escapeHtml = (text) =>
    (text || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // Copy of the current (and was) price in the button bar at the bottom of each DPO step
  const updatePanelPrices = () => {
    if (!priceWrap) return;
    const currentEl = priceWrap.querySelector('.price__current .js-value');
    const wasEl = priceWrap.querySelector('.price__was .js-value');
    const now = currentEl ? currentEl.textContent.trim() : '';
    if (!now) return;
    const was = wasEl && wasEl.offsetParent !== null ? wasEl.textContent.trim() : '';
    const html =
      `<span class="dpo-panel-price__now">${escapeHtml(now)}</span>` +
      (was && was !== now ? ` <s class="dpo-panel-price__was">${escapeHtml(was)}</s>` : '');

    document.querySelectorAll('#itoris_dynamicproductoptions .dpo-next-wrapper').forEach((wrapper) => {
      let el = wrapper.querySelector('.dpo-panel-price');
      if (!el) {
        el = document.createElement('div');
        el.className = 'dpo-panel-price';
        wrapper.prepend(el);
      }
      if (el.innerHTML !== html) el.innerHTML = html;
    });

    // Mobile: the same price sits just above Add to cart instead of in the options bar
    // Mobile: the same price sits just above Add to cart (left), with the "Save" pill on the right
    const atcPrice = document.querySelector('.js-atc-mobile-price');
    if (atcPrice) {
      const amount = atcPrice.querySelector('.atc-mobile-price__amount') || atcPrice.appendChild(Object.assign(document.createElement('span'), { className: 'atc-mobile-price__amount' }));
      if (amount.innerHTML !== html) amount.innerHTML = html;
      atcPrice.hidden = false;
    }
  };

  // Copy the "Save £X (Y%)" text into the mobile price row (same pill styles, incl. seasonal prefix)
  const updateAtcSaving = () => {
    const atcPrice = document.querySelector('.js-atc-mobile-price');
    if (!atcPrice || !savingEl) return;
    let pill = atcPrice.querySelector('.price-saving');
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'price-saving';
      atcPrice.append(pill);
    }
    setText(pill, savingEl.textContent);
    pill.hidden = savingEl.hidden;
  };

  const updatePrice = () => {
    if (!priceWrap) return;
    const currentEl = priceWrap.querySelector('.price__current .js-value');
    const wasEl = priceWrap.querySelector('.price__was .js-value');
    updatePanelPrices();

    if (savingEl && config.showSaving) {
      const now = parseMoney(currentEl && currentEl.textContent);
      const was = parseMoney(wasEl && wasEl.textContent);
      if (now !== null && was !== null && was > now) {
        const saving = was - now;
        const pct = Math.round((saving / was) * 100);
        setText(savingEl, `Save ${formatSaving(saving, currentEl.textContent)} (${pct}%)`);
        savingEl.hidden = false;
      } else {
        savingEl.hidden = true;
      }
      updateAtcSaving();
    }

    if (stickyPanel) {
      const mainPrice = priceWrap.querySelector('.price');
      const stickyPrice = stickyPanel.querySelector('.price');
      if (mainPrice && stickyPrice && stickyPrice.innerHTML !== mainPrice.innerHTML) {
        stickyPrice.innerHTML = mainPrice.innerHTML;
      }
    }
  };

  if (priceWrap) {
    const schedulePrice = raf(updatePrice);
    new MutationObserver(schedulePrice).observe(priceWrap, { childList: true, subtree: true, characterData: true });
    updatePrice();
  }

  /* ---------- Hide the WhatsApp button while it would cover the options / Add to cart ---------- */
  // body.hide-whatsapp is set while the DPO panel or the Add to cart button is on screen
  // (only applied on mobile, see product-conversion.css)
  const whatsappTargets = new Set();
  const whatsappObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) whatsappTargets.add(entry.target);
            else whatsappTargets.delete(entry.target);
          });
          document.body.classList.toggle('hide-whatsapp', whatsappTargets.size > 0);
        })
      : null;

  const watchForWhatsapp = (el) => {
    if (whatsappObserver && el) whatsappObserver.observe(el);
  };

  watchForWhatsapp(document.querySelector('.js-product-form-main'));
  if (document.getElementById('itoris_dynamicproductoptions')) {
    watchForWhatsapp(document.getElementById('itoris_dynamicproductoptions'));
  } else if (whatsappObserver) {
    const panelWaiter = new MutationObserver(() => {
      const panel = document.getElementById('itoris_dynamicproductoptions');
      if (panel) {
        panelWaiter.disconnect();
        watchForWhatsapp(panel);
      }
    });
    panelWaiter.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- DPO options panel ---------- */

  if (!config.dpoHelpers) return;

  // DPO hides the theme's Add to cart and puts its own copy (dpo_add2cart, onclick -> dpoOptions.addToCart)
  // in front of it. Clicking that copy is what adds the chosen options, so prefer it over the hidden original.
  const mainAddButton = () => {
    const form = document.querySelector('.js-product-form-main');
    if (!form) return null;
    return (
      form.querySelector('[dpo_add2cart]') ||
      Array.from(form.querySelectorAll('[name="add"]')).find((btn) => btn.offsetParent !== null) ||
      form.querySelector('[name="add"]')
    );
  };

  // Fabric samples link goes to the request fabric samples page
  const openFabricSamples = () => {
    if (config.fabricSamplesUrl) window.location.href = config.fabricSamplesUrl;
  };

  const canShowFabricLink = () =>
    config.fabricSamplesText &&
    config.fabricSamplesUrl;

  const updateDpo = (root) => {
    // The tabs are built by DPO's custom JS (window.dpoTabs) on a fixed 800ms timer after the options load.
    // Build them as soon as DPO has finished initialising (its own Add to cart exists) so the spinner clears sooner.
    if (
      !document.body.classList.contains('has_dpo_tabs') &&
      typeof window.dpoTabs === 'function' &&
      root.querySelector('.legend') &&
      document.querySelector('[dpo_add2cart]')
    ) {
      try {
        window.dpoTabs();
      } catch (e) {
        /* DPO's own timer will build them instead */
      }
    }

    // Hide DPO's loading spinner as soon as the options are usable (tabs built + DPO's Add to cart in place).
    // The custom JS hides it from jQuery events, which are missed when the app's jQuery and the theme's differ,
    // leaving the spinner up until its 6s failsafe.
    const spinner = root.closest('.dpo-container')?.querySelector('.dpo-inline-loader:not(.hidden)') ||
      document.querySelector('.dpo-inline-loader:not(.hidden)');
    if (spinner && root.querySelector('.dpo-tabs-header') && document.querySelector('[dpo_add2cart]')) {
      spinner.classList.add('hidden');
    }

    const header = root.querySelector('.dpo-tabs-header');
    if (!header) return;

    // Put the steps in the store's preferred order (Theme settings > Product page > Step order)
    reorderSteps(root, header);

    // Only count tabs DPO is showing, so "Step X of Y" matches the numbered tabs (CSS counter skips hidden ones)
    const tabs = Array.from(header.querySelectorAll('.legend')).filter((tab) => tab.getClientRects().length > 0);
    if (!tabs.length) return;

    // DPO's tabs are plain headings with click handlers: make them reachable and usable by keyboard
    tabs.forEach((tab) => {
      if (tab.dataset.hbKeyboard) return;
      tab.dataset.hbKeyboard = '1';
      tab.tabIndex = 0;

      tab.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          tab.click();
        }
      });
    });
    tabs.forEach((tab) => {
      // Only write when it changes: every write re-triggers the panel observer (a constant loop otherwise)
      const current = tab.classList.contains('active');
      if (current && tab.getAttribute('aria-current') !== 'step') tab.setAttribute('aria-current', 'step');
      else if (!current && tab.hasAttribute('aria-current')) tab.removeAttribute('aria-current');
    });

    // Step counter
    let progress = root.querySelector('.dpo-progress');
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'dpo-progress';
      progress.setAttribute('aria-live', 'polite');
      progress.innerHTML =
        '<span class="dpo-progress__step"></span><span class="dpo-progress__name"></span>' +
        '<span class="dpo-progress__bar" aria-hidden="true"></span>';
      header.parentNode.insertBefore(progress, header);
    }
    const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains('active')));
    setText(progress.querySelector('.dpo-progress__step'), `Step ${activeIndex + 1} of ${tabs.length}`);
    setText(progress.querySelector('.dpo-progress__name'), tabs[activeIndex].textContent.trim());
    const width = `${Math.round(((activeIndex + 1) / tabs.length) * 100)}%`;
    const bar = progress.querySelector('.dpo-progress__bar');
    if (bar.style.getPropertyValue('--dpo-progress') !== width) bar.style.setProperty('--dpo-progress', width);

    // Free fabric samples link in the colour / material tab
    if (canShowFabricLink()) {
      const colourTab = tabs.find((tab) => /colou?r|fabric|material/i.test(tab.textContent));
      const fieldset = colourTab && document.getElementById(colourTab.getAttribute('for'));
      if (fieldset && !fieldset.querySelector('.dpo-fabric-samples')) {
        const box = document.createElement('div');
        box.className = 'dpo-fabric-samples';
        const label = document.createElement('span');
        label.textContent = config.fabricSamplesText;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dpo-fabric-samples__btn';
        btn.textContent = config.fabricSamplesButton || 'Order fabric samples';
        btn.addEventListener('click', openFabricSamples);
        box.append(label, btn);
        const next = fieldset.querySelector('.dpo-next-wrapper');
        fieldset.insertBefore(box, next || null);
      }
    }

    // "Expand" link under each colour in the colour / material tab -> large swatch popup
    addSwatchExpanders(tabs);

    // Icons + tidy name/price layout on DPO's text-only choices (mattress size, delivery, disposal, base type)
    decorateTextOptions(tabs);

    // Fabric filter buttons (All / Chenille / Crushed Velvet / ...) above long colour lists
    addFabricFilter(tabs);

    // Top-to-bottom steps instead of horizontal tabs (Theme settings > Product page)
    const accordion = config.dpoAccordion !== false;
    if (accordion) updateAccordion(root, header, tabs);

    if (!accordion) updateScrollHint(root);

    // Add to cart on the last step, where DPO has no "Next Option" button
    // (added before the panel prices so its bar gets a price too)
    const lastFieldset = document.getElementById(tabs[tabs.length - 1].getAttribute('for'));
    const addButton = mainAddButton();
    if (lastFieldset && addButton && !lastFieldset.querySelector('.dpo-next-btn, .dpo-atc-btn')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'dpo-next-wrapper dpo-atc-wrapper';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dpo-atc-btn font-recoleta';
      btn.textContent = addButton.dataset.addToCartText || 'Add to cart';
      btn.addEventListener('click', () => {
        const target = mainAddButton();
        if (target) target.click();
      });
      wrapper.append(btn);
      lastFieldset.append(wrapper);
    }

    // "Back" button on every step after the first: opens the previous (visible) step, like tapping its tab
    // (not needed with the step rows - every step's heading opens it)
    if (!accordion) tabs.slice(1).forEach((tab) => {
      const fieldset = document.getElementById(tab.getAttribute('for'));
      const bar = fieldset && fieldset.querySelector('.dpo-next-wrapper');
      if (!bar || bar.querySelector('.dpo-back-btn')) return;
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'dpo-back-btn';
      back.setAttribute('aria-label', 'Previous step');
      back.innerHTML = '<span aria-hidden="true">←</span><span class="dpo-back-btn__label">Back</span>';
      back.addEventListener('click', () => {
        const visible = Array.from(header.querySelectorAll('.legend')).filter((t) => t.getClientRects().length > 0);
        const index = visible.findIndex((t) => t.getAttribute('for') === fieldset.id);
        if (index > 0) visible[index - 1].click();
      });
      bar.insertBefore(back, bar.querySelector('.dpo-next-btn, .dpo-atc-btn'));
    });

    updatePanelPrices();
  };

  // "More options" cue: a fade + button pinned just above the step's button bar while
  // there are more choices to scroll to. Lives outside the scroll area so it never fades the buttons.
  function updateScrollHint(root) {
    const content = root.querySelector('.dpo-tabs-content');
    if (!content) return;

    let hint = root.querySelector('.dpo-scroll-more');
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'dpo-scroll-more is-hidden';
      hint.innerHTML =
        '<button type="button" class="dpo-scroll-more__btn">More options' +
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>' +
        '</button>';
      hint.querySelector('button').addEventListener('click', () => {
        content.scrollBy({ top: Math.round(content.clientHeight * 0.7), behavior: 'smooth' });
      });
      root.append(hint);
    }

    if (!content.dataset.scrollHintBound) {
      content.dataset.scrollHintBound = 'true';
      content.addEventListener('scroll', raf(() => updateScrollHint(root)), { passive: true });
    }

    const scrollable = content.scrollHeight > content.clientHeight + 4;
    const atEnd = content.scrollTop + content.clientHeight >= content.scrollHeight - 12;
    hint.classList.toggle('is-hidden', !scrollable || atEnd);
    if (!scrollable || atEnd) return;

    // Sit on top of the visible bottom of the choices: the top of the active step's button bar
    // (DPO keeps it pinned at the bottom), or the bottom of the scroll area
    const active = content.querySelector('.dpo-tab-active') || content;
    const bar = active.querySelector('.dpo-next-wrapper');
    const contentRect = content.getBoundingClientRect();
    let visibleBottom = contentRect.bottom;
    if (bar) {
      const barTop = bar.getBoundingClientRect().top;
      if (barTop > contentRect.top && barTop < visibleBottom) visibleBottom = barTop;
    }
    const bottom = `${Math.max(0, Math.round(root.getBoundingClientRect().bottom - visibleBottom))}px`;
    if (hint.style.bottom !== bottom) hint.style.bottom = bottom;
  }

  // ---------- Swatch zoom: "Expand" under each colour, opening a large-image popup ----------
  const largeImageUrl = (src) => {
    if (!src) return '';
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', '1000');
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.toString();
  };

  /* ---------- Top-to-bottom steps ----------
     DPO's tabs (and its custom JS that switches them) stay in place; the tab bar is only hidden visually.
     A heading row is added above each step's choices: number (or tick once chosen), step name and the
     current choice. Tapping a row opens that step via its DPO tab; "Next Option" confirms and moves on. */

  const stripPrice = (text) =>
    (text || '')
      .replace(/[+-]\s*£\s?[\d,.]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const stepValue = (fieldset) => {
    const names = [];
    fieldset.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach((input) => {
      const choice = input.closest('.field.choice') || input.parentElement;
      const title =
        choice.querySelector('.hb-opt__name') ||
        choice.querySelector('.dpo_title') ||
        choice.querySelector('label > span:not(.price-notice)') ||
        choice.querySelector('label');
      const text = stripPrice(title && title.textContent);
      if (text) names.push(text);
    });
    fieldset.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions && select.selectedOptions[0];
      if (option && option.value) names.push(stripPrice(option.textContent));
    });
    return names.join(', ');
  };

  const scrollToStep = (row) => {
    if (row) setTimeout(() => row.scrollIntoView({ behavior: 'smooth', block: 'start' }), 160);
  };


  function updateAccordion(root, header, tabs) {
    if (!root.classList.contains('hb-accordion')) root.classList.add('hb-accordion');

    Array.from(header.querySelectorAll('.legend')).forEach((legend) => {
      const fieldset = document.getElementById(legend.getAttribute('for'));
      if (!fieldset || !fieldset.parentNode) return;

      let row = fieldset.previousElementSibling;
      if (!row || !row.classList.contains('hb-step')) {
        row = document.createElement('button');
        row.type = 'button';
        row.className = 'hb-step';
        row.innerHTML =
          '<span class="hb-step__num" aria-hidden="true"></span>' +
          '<span class="hb-step__text"><span class="hb-step__name"></span><span class="hb-step__value"></span></span>' +
          '<span class="hb-step__action"></span>';
        row.addEventListener('click', () => {
          if (legend.classList.contains('active')) return;
          legend.click();
          scrollToStep(row);
        });
        fieldset.parentNode.insertBefore(row, fieldset);
      }

      const index = tabs.indexOf(legend);
      if (row.hidden !== (index === -1)) row.hidden = index === -1;
      if (index === -1) return;

      const open = legend.classList.contains('active');
      const value = stepValue(fieldset);
      row.classList.toggle('is-open', open);
      row.classList.toggle('is-done', Boolean(value) && !open);
      if (row.getAttribute('aria-expanded') !== String(open)) row.setAttribute('aria-expanded', String(open));
      setText(row.querySelector('.hb-step__num'), String(index + 1));
      setText(row.querySelector('.hb-step__name'), legend.textContent.trim());
      setText(row.querySelector('.hb-step__value'), open ? '' : value || 'Not chosen yet');
      setText(row.querySelector('.hb-step__action'), open ? '' : value ? 'Change' : 'Choose');
    });

    if (root.dataset.hbAccordionBound) return;
    root.dataset.hbAccordionBound = '1';

    // Picking an option only selects it; customers confirm with "Next Option" to move on.
    // After "Next Option", bring the newly opened step into view

    root.addEventListener('click', (evt) => {
      if (!evt.target.closest('.dpo-next-btn')) return;
      setTimeout(() => {
        const active = root.querySelector('.fieldset-section.dpo-tab-active');
        const row = active && active.previousElementSibling;
        if (row && row.classList.contains('hb-step')) scrollToStep(row);
      }, 60);
    });
  }

  /* ---------- Step order ----------
     Reorders DPO's steps once, when its tabs are first built. Both the (hidden) tab headings and the step
     panels are moved, so DPO's "Next Option" - which walks the headings in page order - follows the new order.
     Each step gets the rank of the keyword that best matches its name: a keyword the name starts with wins
     ("split" for "Split Headboard"), otherwise the first listed keyword it contains. Unmatched steps use the
     "*" line; ties keep DPO's order. */

  const stepRank = (name, keywords, fallback) => {
    const text = name.toLowerCase().trim();
    let best = keywords.findIndex((k) => k !== '*' && text.startsWith(k));
    if (best === -1) best = keywords.findIndex((k) => k !== '*' && text.includes(k));
    return best === -1 ? fallback : best;
  };

  function reorderSteps(root, header) {
    if (root.dataset.hbOrdered) return;
    const keywords = String(config.stepOrder || '')
      .split(/\r?\n/)
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);
    const legends = Array.from(header.querySelectorAll('.legend'));
    const content = root.querySelector('.dpo-tabs-content');
    if (!keywords.length || legends.length < 2 || !content) {
      root.dataset.hbOrdered = '1';
      return;
    }
    root.dataset.hbOrdered = '1';

    const star = keywords.includes('*') ? keywords.indexOf('*') : keywords.length;
    const sorted = legends
      .map((legend, index) => ({ legend, index, rank: stepRank(legend.textContent, keywords, star) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index);
    if (sorted.every((step, i) => step.index === i)) return;

    sorted.forEach(({ legend }) => {
      header.appendChild(legend);
      const fieldset = document.getElementById(legend.getAttribute('for'));
      if (!fieldset || fieldset.parentNode !== content) return;
      const row = fieldset.previousElementSibling;
      if (row && row.classList.contains('hb-step')) content.appendChild(row);
      content.appendChild(fieldset);
    });

    // DPO gave every step but its *original* last one a "Next Option" button. Match the new order:
    // every step but the new last one needs it, and the new last one gets Add to cart (added in updateDpo).
    const fieldsets = sorted
      .map(({ legend }) => document.getElementById(legend.getAttribute('for')))
      .filter(Boolean);
    fieldsets.forEach((fieldset, i) => {
      const isLast = i === fieldsets.length - 1;
      const nextBtn = fieldset.querySelector('.dpo-next-btn');
      if (isLast && nextBtn) {
        nextBtn.closest('.dpo-next-wrapper')?.remove();
      } else if (!isLast && !nextBtn) {
        fieldset.querySelector('.dpo-atc-wrapper')?.remove();
        const wrapper = document.createElement('div');
        wrapper.className = 'dpo-next-wrapper';
        wrapper.innerHTML =
          '<button type="button" class="dpo-next-btn bg-primary text-white rounded-full font-recoleta">Next Option</button>';
        fieldset.append(wrapper);
      }
    });

    // Open the new first step without scrolling the page (DPO's tab click scrolls to the tabs)
    const first = sorted.map((s) => s.legend).find((legend) => legend.getClientRects().length > 0);
    if (first && !first.classList.contains('active')) {
      legends.forEach((legend) => legend.classList.remove('active'));
      first.classList.add('active');
      if (typeof window.maintainTabs === 'function') window.maintainTabs();
    }
  }

  /* ---------- Fabric filter on the colour step ---------- */

  const FABRICS = [
    'Plush Velvet', 'Crushed Velvet', 'Chenille', 'Naples', 'Coniston', 'Marble',
    'Boucle', 'Teddy', 'Linen', 'Leather', 'Velvet', 'Wool',
  ];

  function addFabricFilter(tabs) {
    const colourTab = tabs.find((tab) => /colou?r|fabric|material/i.test(tab.textContent));
    const fieldset = colourTab && document.getElementById(colourTab.getAttribute('for'));
    if (!fieldset || fieldset.querySelector('.hb-fabric-filter')) return;
    const choices = Array.from(fieldset.querySelectorAll('.dpo_swatch')).filter((c) => c.querySelector('img'));
    if (choices.length < 8) return;

    const groupOf = (choice) => {
      const title = ((choice.querySelector('.dpo_title') || choice).textContent || '').toLowerCase();
      return FABRICS.find((fabric) => title.includes(fabric.toLowerCase())) || 'Other';
    };
    const groups = [];
    choices.forEach((choice) => {
      const group = groupOf(choice);
      choice.dataset.hbFabric = group;
      if (!groups.includes(group)) groups.push(group);
    });
    if (groups.length < 2) return;

    const bar = document.createElement('div');
    bar.className = 'hb-fabric-filter';
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Filter colours by fabric');
    ['All'].concat(groups).forEach((group) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hb-fabric-filter__btn';
      const count = group === 'All' ? choices.length : choices.filter((c) => c.dataset.hbFabric === group).length;
      btn.innerHTML = `${escapeHtml(group)} <span class="hb-fabric-filter__count">${count}</span>`;
      btn.setAttribute('aria-pressed', String(group === 'All'));
      btn.addEventListener('click', () => {
        bar.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        choices.forEach((choice) => {
          choice.classList.toggle('hb-fabric-hidden', group !== 'All' && choice.dataset.hbFabric !== group);
        });
      });
      bar.append(btn);
    });
    const list = choices[0].parentElement;
    list.parentNode.insertBefore(bar, list);
  }

  const colourChoices = (fieldset) =>
    Array.from(fieldset.querySelectorAll('.dpo_swatch')).filter(
      (choice) => choice.querySelector('img') && choice.getClientRects().length > 0
    );

  let swatchDialog = null;
  let swatchList = [];
  let swatchIndex = 0;

  const showSwatch = (index) => {
    swatchIndex = (index + swatchList.length) % swatchList.length;
    const choice = swatchList[swatchIndex];
    const img = choice.querySelector('img');
    const title = choice.querySelector('.dpo_title') || choice.querySelector('label');
    const name = title ? title.textContent.trim() : '';
    const big = swatchDialog.querySelector('.swatch-zoom__image');
    big.src = largeImageUrl(img.getAttribute('src'));
    big.alt = name;
    swatchDialog.querySelector('.swatch-zoom__name').textContent = name;
    swatchDialog.querySelector('.swatch-zoom__count').textContent = `${swatchIndex + 1} of ${swatchList.length}`;
    const input = choice.querySelector('input');
    const chosen = input && input.checked;
    const pick = swatchDialog.querySelector('.swatch-zoom__choose');
    pick.textContent = chosen ? 'Selected \u2713' : 'Choose this colour';
    pick.disabled = !!chosen;
  };

  const buildSwatchDialog = () => {
    const dialog = document.createElement('dialog');
    dialog.className = 'swatch-zoom';
    dialog.setAttribute('aria-label', 'Colour preview');
    dialog.innerHTML =
      '<div class="swatch-zoom__inner">' +
      '<button type="button" class="swatch-zoom__close" aria-label="Close">&times;</button>' +
      '<div class="swatch-zoom__stage">' +
      '<button type="button" class="swatch-zoom__nav swatch-zoom__nav--prev" aria-label="Previous colour">&#8249;</button>' +
      '<img class="swatch-zoom__image" alt="">' +
      '<button type="button" class="swatch-zoom__nav swatch-zoom__nav--next" aria-label="Next colour">&#8250;</button>' +
      '</div>' +
      '<div class="swatch-zoom__footer">' +
      '<div><p class="swatch-zoom__name"></p><p class="swatch-zoom__count"></p></div>' +
      '<button type="button" class="swatch-zoom__choose"></button>' +
      '</div>' +
      '<p class="swatch-zoom__note">Colours can look slightly different on screen. Order fabric samples to see them in person.</p>' +
      '</div>';
    dialog.querySelector('.swatch-zoom__close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.swatch-zoom__nav--prev').addEventListener('click', () => showSwatch(swatchIndex - 1));
    dialog.querySelector('.swatch-zoom__nav--next').addEventListener('click', () => showSwatch(swatchIndex + 1));
    dialog.querySelector('.swatch-zoom__choose').addEventListener('click', () => {
      const choice = swatchList[swatchIndex];
      const target = choice && (choice.querySelector('label') || choice.querySelector('input'));
      if (target) target.click();
      dialog.close();
    });
    // Clicking the dark backdrop closes
    dialog.addEventListener('click', (evt) => {
      if (evt.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (evt) => {
      if (evt.key === 'ArrowLeft') showSwatch(swatchIndex - 1);
      if (evt.key === 'ArrowRight') showSwatch(swatchIndex + 1);
    });
    document.body.append(dialog);
    return dialog;
  };

  const openSwatchZoom = (fieldset, choice) => {
    swatchList = colourChoices(fieldset);
    if (!swatchList.length) return;
    if (!swatchDialog) swatchDialog = buildSwatchDialog();
    showSwatch(Math.max(0, swatchList.indexOf(choice)));
    if (typeof swatchDialog.showModal === 'function') swatchDialog.showModal();
    else swatchDialog.setAttribute('open', '');
  };

  /* ---------- Icons for text-only choices ---------- */

  // Same visual line weight whatever the drawing's grid size (1.8 on a 40-unit grid)
  const svg = (body, viewBox = '0 0 40 40') =>
    `<svg viewBox="${viewBox}" width="40" height="40" fill="none" stroke="currentColor" ` +
    `stroke-width="${((1.8 * Number(viewBox.split(' ')[2])) / 40).toFixed(2)}" ` +
    `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${body}</svg>`;

  // Top-down mattress drawn to scale: widest (6ft, 180cm) fills the icon
  const mattressIcon = (widthCm) => {
    const w = Math.round((widthCm / 180) * 30);
    const x = (40 - w) / 2;
    const pillowCount = widthCm >= 110 ? 2 : 1;
    const gap = 2;
    const pw = (w - 4 - gap * (pillowCount - 1)) / pillowCount;
    let pillows = '';
    for (let i = 0; i < pillowCount; i += 1) {
      pillows += `<rect x="${x + 2 + i * (pw + gap)}" y="7" width="${pw}" height="5" rx="1.5"/>`;
    }
    return svg(`<rect x="${x}" y="4" width="${w}" height="32" rx="3"/>${pillows}<path d="M${x} 17h${w}" opacity=".45"/>`);
  };

  const ICONS = {
    doorstep: svg('<path d="M9 35V6h15v29"/><path d="M20 21h.01"/><rect x="25" y="25" width="10" height="10" rx="1"/><path d="M25 29h10M30 25v4"/><path d="M5 35h32"/>'),
    room: svg('<path d="M5 18 20 6l15 12"/><path d="M9 15v20h22V15"/><path d="M14 35v-8h12v8"/><path d="M14 27v-3h12v3"/>'),
    assembly: svg('<path d="M24.5 8.5a6 6 0 0 0-7.6 7.6L6.5 26.5a2.8 2.8 0 0 0 4 4l10.4-10.4a6 6 0 0 0 7.6-7.6l-3.7 3.7-3.4-.6-.6-3.4z"/><path d="M27 24l7 7M29.5 21.5l5 5"/>'),
    // Recycling symbol (after Lucide's "recycle" icon, ISC licence)
    recycle: svg(
      '<path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5"/>' +
        '<path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12"/>' +
        '<path d="m14 16-3 3 3 3"/><path d="M8.293 13.596 7.196 9.5 3.1 10.598"/>' +
        '<path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843"/>' +
        '<path d="m13.378 9.633 4.096 1.098 1.097-4.096"/>',
      '0 0 24 24'
    ),
    none: svg('<circle cx="20" cy="20" r="13"/><path d="m11 29 18-18"/>'),
    yes: svg('<circle cx="20" cy="20" r="13"/><path d="m14 20.5 4 4 8-9"/>'),
    base: svg('<rect x="5" y="14" width="30" height="12" rx="2"/><path d="M9 26v4M31 26v4"/>'),
    reinforced: svg('<rect x="5" y="14" width="30" height="12" rx="2"/><path d="M12.5 14v12M20 14v12M27.5 14v12"/><path d="M9 26v4M20 26v4M31 26v4"/>'),
  };

  const SIZES = [
    [/super\s*king|6\s*ft/i, 180],
    [/king|5\s*ft/i, 150],
    [/small\s*double|4\s*ft(?!\s*6)(?!')/i, 120],
    [/4\s*'\s*6|4\s*ft\s*6|double/i, 135],
    [/small\s*single|2\s*ft\s*6/i, 75],
    [/single|3\s*ft/i, 90],
  ];

  const iconFor = (tabName, text) => {
    const t = text.toLowerCase();
    const tab = (tabName || '').toLowerCase();
    if (/size|mattress config/.test(tab) || SIZES.some(([re]) => re.test(text))) {
      const match = SIZES.find(([re]) => re.test(text));
      if (match) return mattressIcon(match[1]);
    }
    if (/doorstep|kerbside|curbside/.test(t)) return ICONS.doorstep;
    if (/room of choice|room/.test(t)) return ICONS.room;
    if (/assembl/.test(t)) return ICONS.assembly;
    if (/dispos|recycl|remov/.test(t)) return ICONS.recycle;
    if (/reinforc/.test(t)) return ICONS.reinforced;
    if (/base/.test(t)) return ICONS.base;
    if (/^no\b|not required|^none/.test(t)) return ICONS.none;
    if (/^yes\b/.test(t)) return ICONS.yes;
    return '';
  };

  function decorateTextOptions(tabs) {
    tabs.forEach((tab) => {
      const fieldset = document.getElementById(tab.getAttribute('for'));
      if (!fieldset) return;
      const tabName = tab.textContent.trim();
      const isDelivery = /deliver/i.test(tabName);
      fieldset.querySelectorAll('.dpo_swatch_text').forEach((choice) => {
        if (choice.dataset.hbDecorated) return;
        const label = choice.querySelector('label');
        const name = label && label.querySelector('span:not(.price-notice)');
        if (!name) return;
        choice.dataset.hbDecorated = '1';
        choice.classList.add('hb-opt');
        if (choice.parentElement) choice.parentElement.classList.add('hb-opt-grid');
        name.classList.add('hb-opt__name');
        const icon = iconFor(tabName, name.textContent.trim());
        if (icon) {
          const holder = document.createElement('span');
          holder.className = 'hb-opt__icon';
          holder.innerHTML = icon;
          label.prepend(holder);
        } else {
          choice.classList.add('hb-opt--no-icon');
        }
        const price = label.querySelector('.price-notice');
        if (price) {
          price.classList.add('hb-opt__price');
        } else if (isDelivery) {
          const free = document.createElement('span');
          free.className = 'hb-opt__price hb-opt__price--free';
          free.textContent = 'Free';
          label.append(free);
        }
      });
    });
  }

  function addSwatchExpanders(tabs) {
    const colourTab = tabs.find((tab) => /colou?r|fabric|material/i.test(tab.textContent));
    const fieldset = colourTab && document.getElementById(colourTab.getAttribute('for'));
    if (!fieldset) return;
    fieldset.querySelectorAll('.dpo_swatch').forEach((choice) => {
      if (!choice.querySelector('img') || choice.querySelector('.swatch-expand')) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch-expand';
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" ' +
        'stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2M11 8v6M8 11h6"/></svg>' +
        '<span>Expand</span>';
      btn.setAttribute('aria-label', `Expand ${(choice.querySelector('.dpo_title') || choice).textContent.trim()}`);
      // Don't let the click select the colour
      ['click', 'mousedown', 'pointerdown', 'touchstart'].forEach((type) =>
        btn.addEventListener(type, (evt) => evt.stopPropagation(), { passive: type === 'touchstart' })
      );
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        openSwatchZoom(fieldset, choice);
      });
      const title = choice.querySelector('.dpo_title');
      if (title) title.after(btn);
      else choice.append(btn);
    });
  }

  // DPO renders asynchronously - wait for its panel, then keep the helpers in sync with it
  const start = (root) => {
    const schedule = raf(() => updateDpo(root));
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'style'],
    });
    root.addEventListener('change', schedule);
    root.addEventListener('click', schedule);
    window.addEventListener('resize', schedule);
    updateDpo(root);
  };

  const existing = document.getElementById('itoris_dynamicproductoptions');
  if (existing) {
    start(existing);
  } else {
    const waiter = new MutationObserver(() => {
      const root = document.getElementById('itoris_dynamicproductoptions');
      if (root) {
        waiter.disconnect();
        start(root);
      }
    });
    waiter.observe(document.body, { childList: true, subtree: true });
  }
})();

// Stars under the product title: open the Reviews tab (sections/product-tabs.liquid) and scroll to it
document.addEventListener('click', (evt) => {
  const link = evt.target.closest('.js-reviews-jump');
  if (!link) return;
  const reviews = document.getElementById('reviews');
  const tabButton = document.querySelector('.tab-button[data-tab="reviews"]');
  if (!reviews) return;
  evt.preventDefault();
  if (tabButton) tabButton.click();
  else reviews.classList.remove('hidden');
  const target = (tabButton && tabButton.closest('nav')) || reviews;
  setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
});
